import { omit } from 'lodash';
import { apolloClient } from '~/apollo-client';
import {
  Flex_FetchFunnelPacketQuery,
  Flex_FetchFunnelPacketQueryVariables,
  UserOriginationSource,
} from '~/typings/graphql';
import { UpdateUserMetaDataParams } from '../update-user-meta-data';
import { FETCH_FUNNEL_PACKET } from './fetch-funnel-packet.queries';

export const fetchFunnelPacket = async (
  params: FetchFunnelPacketParams,
): Promise<FetchFunnelPacketResponse> => {
  const { data } = await apolloClient.query<
    Flex_FetchFunnelPacketQuery,
    Flex_FetchFunnelPacketQueryVariables
  >({
    query: FETCH_FUNNEL_PACKET,
    variables: {
      accountId: params.accountId,
      customPlanId: params.customPlanId,
      userId: params.userId,
      utilityAccountId: params.utilityAccountId,
    },
    fetchPolicy: 'no-cache',
  });

  return {
    nonLmi: data.funnelPacket.nonLmi,
    inviteCode: data.funnelPacket.inviteCode,
    account: data.funnelPacket.utilityAccount.account,
    funnelVnmProjectId: data.funnelPacket.funnelVnmProjectId,
    signupContractTemplateId: data.funnelPacket.signupContractTemplate?.id,
    signupContractTemplate: data.funnelPacket.signupContractTemplate,
    customPlan: {
      ...omit(data.funnelPacket.customPlan, ['planTemplate']),
      billingAddress: {
        ...data.funnelPacket.customPlan.billingAddress,
        id: data.funnelPacket.customPlan.billingAddress?.id,
      },
      utilityAccounts: data.funnelPacket.customPlan.utilityAccounts || [],
    },
    planTemplate: data.funnelPacket.customPlan.planTemplate,
    signedContractIds: data.funnelPacket.signedContractIds,
    utilityAccount: omit(data.funnelPacket.utilityAccount, [
      'account',
      'utilityCredential',
      'user',
      'utility',
    ]),
    utility: data.funnelPacket.utilityAccount.utility,
    onboardingRateClasses: data.funnelPacket.onboardingRateClasses,
    utilityCredential: data.funnelPacket.utilityAccount.utilityCredential,
    user: data.funnelPacket.utilityAccount.user,
    unparsedQueryParams: data.funnelPacket.unparsedQueryParams,
    meter: data.funnelPacket.meter,
    projectSnapshot: data.funnelPacket.projectSnapshot,
  };
};

export type FetchFunnelPacketParams = {
  accountId?: number;
  customPlanId?: number;
  userId: number;
  utilityAccountId?: number;
};

type Packet = Flex_FetchFunnelPacketQuery['funnelPacket'];

type CustomPlanWithBillingAddress = Packet['customPlan'] & {
  billingAddress?: any;
};

export interface FetchFunnelPacketResponse {
  account: Packet['utilityAccount']['account'];
  customPlan: Omit<CustomPlanWithBillingAddress, 'planTemplate'> & {
    billingAddress?: CustomPlanWithBillingAddress['billingAddress'];
    utilityAccounts?: CustomPlanWithBillingAddress['utilityAccounts'];
  };
  planTemplate: Packet['customPlan']['planTemplate'];
  signedContractIds: Packet['signedContractIds'];
  nonLmi: Packet['nonLmi'];
  inviteCode: Packet['inviteCode'];
  user: FunnelPacketUser;
  funnelVnmProjectId?: number | null;
  signupContractTemplateId?: number | null;
  signupContractTemplate?: Packet['signupContractTemplate'];
  utility: Packet['utilityAccount']['utility'];
  onboardingRateClasses: Packet['onboardingRateClasses'];
  utilityAccount: Omit<
    Packet['utilityAccount'],
    'account' | 'user' | 'utility' | 'utilityCredential'
  >;
  utilityCredential: Packet['utilityAccount']['utilityCredential'];
  unparsedQueryParams?: Packet['unparsedQueryParams'];
  utilityAccountMissingError?: Packet['utilityAccount']['utilityAccountMissingError'];
  meter?: Packet['meter'];
  projectSnapshot?: Packet['projectSnapshot'];
}

type FunnelPacketUser = {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string | null;
  zipCode?: string | null | undefined;
  promoCode?: string | undefined;
  phone?: string | null;
  metaData?: UpdateUserMetaDataParams;
  originationSource?: FunnelPacketUserOriginationSource | null;
  signupPromoCode?: { code: string } | null | undefined;
  signupFunnelLead?: {
    paymentService?: {
      zuoraPaymentMethodId?: string;
      [key: string]: unknown;
    } | null;
  } | null;
  nonLmi?: boolean;
  inviteCode?: string;
};

export interface FunnelPacketUserOriginationSource
  extends Pick<
    UserOriginationSource,
    | 'funnel'
    | 'originationSourceType'
    | 'queryParams'
    | 'funnelVnmProjectId'
    | 'enrollmentExperience'
  > {}
